import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";

const initialForm = { first_name: "", last_name: "", email: "", whatsapp_number: "" };
const GOOGLE_SHEETS_URL = process.env.REACT_APP_GOOGLE_SHEETS_URL;

const fireConfetti = () => {
  confetti({
    particleCount: 90,
    spread: 70,
    startVelocity: 32,
    decay: 0.92,
    scalar: 0.9,
    origin: { y: 0.6 },
    colors: ["#0284c7", "#38bdf8", "#0f172a", "#e0f2fe"],
  });
};

const logToGoogleSheet = async (payload) => {
  if (!GOOGLE_SHEETS_URL) {
    throw new Error("The waitlist form is not configured.");
  }

  await fetch(GOOGLE_SHEETS_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: JSON.stringify(payload),
  });
};

export const SignupCard = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const handleScrollToDemo = () => {
    document.getElementById("sound-mapping-demo-card")?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("loading");
    try {
      await logToGoogleSheet(form);
      setStatus("success");
      fireConfetti();
    } catch (err) {
      setStatus("idle");
      setError(
        err?.response?.data?.detail
          ? String(err.response.data.detail)
          : "Something went wrong. Please try again."
      );
    }
  };

  return (
    <motion.div
      id="join"
      data-testid="signup-card"
      className="relative mx-auto w-full max-w-xl rounded-[32px] border border-slate-200/80 bg-white/90 p-6 shadow-[0_25px_60px_-15px_rgba(2,132,199,0.18)] backdrop-blur-xl transition-shadow duration-300 hover:shadow-[0_30px_70px_-12px_rgba(2,132,199,0.24)] sm:p-10"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
    >
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            data-testid="success-confetti-card"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center justify-center gap-4 py-10 text-center"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-sky-100">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <p className="font-body max-w-sm text-base leading-relaxed text-slate-600 sm:text-lg">
              Thanks for showing interest! 🥳 When the beta release is ready for you
              to try, I’ll reach out to you via email and/or WhatsApp. Good things
              come to those who wait 😉
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            data-testid="waitlist-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-5"
          >
            <div className="text-center">
              <span className="font-mono-accent text-xs font-semibold uppercase tracking-[0.25em] text-sky-600">
                Beta Access
              </span>
              <h3 className="font-display mt-2 text-xl font-bold text-slate-900 sm:text-2xl">
                Join the Free Beta List
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="first_name" className="font-body text-sm text-slate-600">
                  First Name
                </Label>
                <Input
                  id="first_name"
                  data-testid="first-name-input"
                  placeholder="Ada"
                  required
                  value={form.first_name}
                  onChange={handleChange("first_name")}
                  className="h-12 rounded-2xl border-slate-200 bg-slate-50/60 px-4 focus-visible:ring-sky-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="last_name" className="font-body text-sm text-slate-600">
                  Last Name
                </Label>
                <Input
                  id="last_name"
                  data-testid="last-name-input"
                  placeholder="Lovelace"
                  required
                  value={form.last_name}
                  onChange={handleChange("last_name")}
                  className="h-12 rounded-2xl border-slate-200 bg-slate-50/60 px-4 focus-visible:ring-sky-500"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="font-body text-sm text-slate-600">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                placeholder="ada@example.com"
                required
                value={form.email}
                onChange={handleChange("email")}
                className="h-12 rounded-2xl border-slate-200 bg-slate-50/60 px-4 focus-visible:ring-sky-500"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="whatsapp_number" className="font-body text-sm text-slate-600">
                WhatsApp Number
              </Label>
              <Input
                id="whatsapp_number"
                type="tel"
                data-testid="whatsapp-input"
                placeholder="+1 555 000 1234"
                required
                value={form.whatsapp_number}
                onChange={handleChange("whatsapp_number")}
                className="h-12 rounded-2xl border-slate-200 bg-slate-50/60 px-4 focus-visible:ring-sky-500"
              />
            </div>

            {error && (
              <p data-testid="form-error-message" className="text-center text-sm font-medium text-red-500">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="join-beta-button"
              disabled={status === "loading"}
              className="h-14 w-full rounded-2xl bg-slate-900 font-display text-base font-bold uppercase tracking-wide text-white shadow-[0_15px_35px_-10px_rgba(15,23,42,0.4)] transition-colors duration-200 hover:bg-sky-600 disabled:opacity-60"
            >
              {status === "loading" ? "Joining…" : "Join the Free Beta List"}
            </Button>
            <p
              data-testid="scarcity-nudge"
              className="text-center font-mono-accent text-xs font-semibold uppercase tracking-[0.15em] text-sky-600"
            >
              Limited to the first 500 early adopters
            </p>
            <Button
              type="button"
              data-testid="see-how-it-works-button"
              onClick={handleScrollToDemo}
              variant="outline"
              className="h-12 w-full rounded-2xl border-slate-200 bg-white/70 font-display text-sm font-semibold uppercase tracking-wide text-slate-600 shadow-none transition-colors duration-200 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
            >
              See How It Works
            </Button>
            <p className="text-center font-body text-xs text-slate-400">
              No spam. Just one email when the beta opens.
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
